import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import {
  Shield,
  LogOut,
  ListChecks,
  UserCheck,
  FileText,
  Menu,
  X,
  Link,
  Hash
} from 'lucide-react';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navItems = [
    { path: '/admin/progress', label: 'Progress', icon: ListChecks },
    { path: '/admin/whitelist', label: 'Whitelist', icon: UserCheck },
    { path: '/admin/submissions', label: 'Submissions', icon: FileText },
    { path: '/admin/links', label: 'Links', icon: Link },
    { path: '/admin/numbers', label: 'Numbers', icon: Hash },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/10">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="bg-accent/10 p-1.5 sm:p-2 rounded-lg">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold text-foreground">IFTA Portal</h1>
                <p className="text-xs sm:text-sm text-muted-foreground">Admin</p>
              </div>
            </div>

            {/* Desktop User Info */}
            <div className="hidden md:flex items-center gap-4">
              <Badge variant="outline" className="text-accent border-accent">
                Admin
              </Badge>
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={toggleMobileMenu}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:block sticky top-[73px] z-40 bg-background/80 backdrop-blur-sm border-b">
        <div className="container mx-auto px-4">
          <nav className="flex gap-1 py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-md transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/10'
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Mobile Navigation - Horizontal Scrollable */}
      <div className="md:hidden sticky top-[73px] z-40 bg-background/80 backdrop-blur-sm border-b">
        <div className="container mx-auto px-4">
          <nav className="flex gap-1 py-2 overflow-x-auto scrollbar-hide">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/10'
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-b bg-background/95">
          <div className="container mx-auto px-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="text-accent border-accent">
                  Admin
                </Badge>
                <span className="text-muted-foreground">{user?.email}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="w-full justify-start"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <div className="container mx-auto px-4 py-4 sm:py-6 h-[calc(100vh-121px)] md:h-[calc(100vh-125px)] overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}